/**
 * قراءة أول ورقة من ملف Excel (XLSX) إلى صفوف نصية تدخل نفس مسار CSV
 * (`buildStatement`). بلا مكتبة: XLSX ملف zip فيه XML، والفك بـ `DecompressionStream`
 * المدمج فى المتصفح. يكفى لكشوف البنوك (قيم وتواريخ ونصوص)، ولا يقرأ الصيغ
 * ولا التنسيق — يأخذ القيمة المحفوظة لكل خلية كما حسبها Excel.
 *
 * `.xls` القديم (ثنائى) غير مدعوم: يُحفظ من Excel بصيغة xlsx أو CSV.
 */

/** سقف الحجم بعد الفك — كشف بنك لا يقترب منه، وما فوقه ملف مضغوط مفخخ أو ليس كشفًا. */
const MAX_UNCOMPRESSED_BYTES = 50 * 1024 * 1024;

export class XlsxError extends Error {}

interface ZipEntry {
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

function readZipDirectory(bytes: Uint8Array): Map<string, ZipEntry> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // نهاية الدليل المركزى: آخر ٢٢ بايت + تعليق اختيارى حتى ٦٤ ك.ب
  let end = -1;
  for (
    let offset = bytes.length - 22;
    offset >= Math.max(0, bytes.length - 22 - 65535);
    offset -= 1
  ) {
    if (view.getUint32(offset, true) === 0x06054b50) {
      end = offset;
      break;
    }
  }
  if (end < 0) throw new XlsxError('الملف ليس ملف Excel (xlsx) صالحًا.');
  const count = view.getUint16(end + 10, true);
  let offset = view.getUint32(end + 16, true);
  const entries = new Map<string, ZipEntry>();
  const decoder = new TextDecoder();
  for (let index = 0; index < count; index += 1) {
    if (offset + 46 > bytes.length || view.getUint32(offset, true) !== 0x02014b50) {
      throw new XlsxError('ملف Excel تالف.');
    }
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
    entries.set(name, {
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      uncompressedSize: view.getUint32(offset + 24, true),
      localHeaderOffset: view.getUint32(offset + 42, true),
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

async function inflateRaw(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.length;
    // السقف أثناء الفك لا بعده: الحجم المعلن فى الدليل قد يكذب
    if (total > MAX_UNCOMPRESSED_BYTES) {
      await reader.cancel();
      throw new XlsxError('الملف أكبر من أن يكون كشف بنك.');
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let position = 0;
  for (const chunk of chunks) {
    out.set(chunk, position);
    position += chunk.length;
  }
  return out;
}

async function readEntry(
  bytes: Uint8Array<ArrayBuffer>,
  entries: Map<string, ZipEntry>,
  name: string,
): Promise<string | null> {
  const entry = entries.get(name);
  if (!entry) return null;
  if (entry.uncompressedSize > MAX_UNCOMPRESSED_BYTES) {
    throw new XlsxError('الملف أكبر من أن يكون كشف بنك.');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const local = entry.localHeaderOffset;
  if (local + 30 > bytes.length || view.getUint32(local, true) !== 0x04034b50) {
    throw new XlsxError('ملف Excel تالف.');
  }
  const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
  const data = bytes.subarray(start, start + entry.compressedSize);
  let raw: Uint8Array;
  if (entry.method === 0) raw = data;
  else if (entry.method === 8) raw = await inflateRaw(data);
  else throw new XlsxError('ضغط غير مدعوم داخل ملف Excel.');
  return new TextDecoder().decode(raw);
}

function decodeXml(text: string): string {
  return text
    .replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, entity: string) => {
      const lower = entity.toLowerCase();
      if (lower === 'amp') return '&';
      if (lower === 'lt') return '<';
      if (lower === 'gt') return '>';
      if (lower === 'quot') return '"';
      if (lower === 'apos') return "'";
      const code = lower.startsWith('#x')
        ? parseInt(lower.slice(2), 16)
        : parseInt(lower.slice(1), 10);
      return String.fromCodePoint(code);
    })
    .replace(/_x([0-9a-f]{4})_/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
}

function attributes(source: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const match of source.matchAll(/([\w:]+)\s*=\s*"([^"]*)"/g)) {
    out[match[1]!] = decodeXml(match[2]!);
  }
  return out;
}

/** نص عنصر غنى (`<si>` أو `<is>`): كل `<t>` متجاورة، بلا النطق الصوتى `<rPh>`. */
function richText(xml: string): string {
  const plain = xml.replace(/<rPh\b[\s\S]*?<\/rPh>/g, '');
  let text = '';
  for (const match of plain.matchAll(/<t\b[^>]*?(?:\/>|>([\s\S]*?)<\/t>)/g)) {
    text += decodeXml(match[1] ?? '');
  }
  return text;
}

function sharedStrings(xml: string | null): string[] {
  if (!xml) return [];
  return Array.from(xml.matchAll(/<si\b[^>]*?(?:\/>|>([\s\S]*?)<\/si>)/g), (match) =>
    richText(match[1] ?? ''),
  );
}

/** أرقام تنسيقات التاريخ المدمجة فى Excel. */
const BUILTIN_DATE_FORMATS = new Set([
  14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 30, 36, 45, 46, 47, 50, 57,
]);

function isDateFormatCode(code: string): boolean {
  // يُسقط النص المقتبس والألوان والشروط ثم يبحث عن رموز يوم/شهر/سنة
  const bare = code
    .replace(/"[^"]*"/g, '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/\\./g, '');
  return /[dmy]/i.test(bare) && !/^[#0.,%\s]*$/.test(bare);
}

/** لكل نمط خلية (`s`): هل يعرض رقمه تاريخًا. */
function dateStyles(xml: string | null): boolean[] {
  if (!xml) return [];
  const custom = new Map<number, string>();
  for (const match of xml.matchAll(/<numFmt\b([^>]*)\/?>/g)) {
    const attrs = attributes(match[1]!);
    if (attrs.numFmtId && attrs.formatCode) custom.set(Number(attrs.numFmtId), attrs.formatCode);
  }
  const cellXfs = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(xml)?.[1] ?? '';
  return Array.from(cellXfs.matchAll(/<xf\b([^>]*?)\/?>/g), (match) => {
    const id = Number(attributes(match[1]!).numFmtId ?? '0');
    const code = custom.get(id);
    return code !== undefined ? isDateFormatCode(code) : BUILTIN_DATE_FORMATS.has(id);
  });
}

/** رقم Excel التسلسلى إلى `YYYY-MM-DD` — نظام ١٩٠٠ (بخطأ ٢٩ فبراير الشهير) أو ١٩٠٤. */
export function excelSerialToDate(serial: number, date1904 = false): string | null {
  if (!Number.isFinite(serial) || serial < 1) return null;
  const days = Math.floor(serial);
  // ١٩٠٠: اليوم ٦٠ هو ٢٩ فبراير ١٩٠٠ الوهمى، فالأساس ٣٠ ديسمبر ١٨٩٩ لما بعده
  const base = date1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, days < 60 ? 31 : 30);
  const date = new Date(base + days * 86_400_000);
  return (
    date.getUTCFullYear() +
    '-' +
    String(date.getUTCMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getUTCDate()).padStart(2, '0')
  );
}

/**
 * الرقم كما خزّنه Excel نصًا. تمثيل عشرى طويل (0.30000000000000004) أو أُسّى يُقرّب
 * إلى أربع خانات — حدّ `moneyString` — وإلا رفضه المحلل كمبلغ فاسد وهو سليم.
 */
function numberText(raw: string): string {
  const text = raw.trim();
  if (/^-?\d+(\.\d{1,4})?$/.test(text)) return text;
  const value = Number(text);
  if (!Number.isFinite(value)) return text;
  return value.toFixed(4).replace(/\.?0+$/, '');
}

function columnIndex(reference: string): number {
  const letters = /^[A-Z]+/i.exec(reference)?.[0].toUpperCase() ?? '';
  let index = 0;
  for (const letter of letters) index = index * 26 + (letter.charCodeAt(0) - 64);
  return index - 1;
}

function firstSheetPath(workbook: string | null, rels: string | null): string {
  const fallback = 'xl/worksheets/sheet1.xml';
  if (!workbook || !rels) return fallback;
  const sheet = /<sheet\b([^>]*)\/?>/.exec(workbook);
  const relId = sheet ? attributes(sheet[1]!)['r:id'] : undefined;
  if (!relId) return fallback;
  for (const match of rels.matchAll(/<Relationship\b([^>]*)\/?>/g)) {
    const attrs = attributes(match[1]!);
    if (attrs.Id === relId && attrs.Target) {
      const target = attrs.Target.replace(/^\//, '');
      return target.startsWith('xl/') ? target : 'xl/' + target;
    }
  }
  return fallback;
}

/**
 * أول ورقة إلى صفوف نصوص مقصوصة، بلا صفوف فارغة — نفس شكل `parseCsv`.
 * الخلية الرقمية بتنسيق تاريخ تصير `YYYY-MM-DD` فيفهمها `parseStatementDate`.
 */
export async function parseXlsx(buffer: ArrayBuffer): Promise<string[][]> {
  const bytes = new Uint8Array(buffer);
  if (bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
    throw new XlsxError('الملف ليس ملف Excel (xlsx) صالحًا.');
  }
  const entries = readZipDirectory(bytes);
  const workbook = await readEntry(bytes, entries, 'xl/workbook.xml');
  if (!workbook) throw new XlsxError('الملف ليس ملف Excel (xlsx) صالحًا.');
  const rels = await readEntry(bytes, entries, 'xl/_rels/workbook.xml.rels');
  const sheet = await readEntry(bytes, entries, firstSheetPath(workbook, rels));
  if (!sheet) throw new XlsxError('لم تُوجد ورقة بيانات فى الملف.');
  const strings = sharedStrings(await readEntry(bytes, entries, 'xl/sharedStrings.xml'));
  const dates = dateStyles(await readEntry(bytes, entries, 'xl/styles.xml'));
  const date1904 = /<workbookPr\b[^>]*\bdate1904\s*=\s*"(1|true)"/i.test(workbook);

  const rows: string[][] = [];
  for (const rowMatch of sheet.matchAll(/<row\b[^>]*?(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const cells: string[] = [];
    let next = 0;
    for (const cellMatch of (rowMatch[1] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = attributes(cellMatch[1]!);
      const body = cellMatch[2] ?? '';
      const index = attrs.r ? columnIndex(attrs.r) : next;
      next = index + 1;
      const raw = /<v\b[^>]*>([\s\S]*?)<\/v>/.exec(body)?.[1];
      let value = '';
      switch (attrs.t) {
        case 's':
          value = strings[Number(raw)] ?? '';
          break;
        case 'inlineStr':
          value = richText(/<is\b[^>]*>([\s\S]*?)<\/is>/.exec(body)?.[1] ?? '');
          break;
        case 'str':
          value = decodeXml(raw ?? '');
          break;
        case 'b':
          value = raw === '1' ? 'TRUE' : raw === '0' ? 'FALSE' : '';
          break;
        case 'd':
          value = /^\d{4}-\d{2}-\d{2}/.exec(raw ?? '')?.[0] ?? '';
          break;
        case 'e':
          value = '';
          break;
        default:
          if (raw !== undefined) {
            const style = attrs.s !== undefined ? Number(attrs.s) : 0;
            value = dates[style]
              ? (excelSerialToDate(Number(raw), date1904) ?? '')
              : numberText(decodeXml(raw));
          }
      }
      while (cells.length < index) cells.push('');
      cells[index] = value.trim();
    }
    if (cells.some((value) => value !== '')) rows.push(cells);
  }
  return rows;
}
